import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  HandCoins, CheckCircle2, XCircle, Clock, Loader2, Building2,
  DollarSign, Calendar, Phone, Mail, Hash, X, Check, AlertCircle,
  Infinity as InfinityIcon, Copy, Zap, Ban, BookOpen, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_approval: { label: 'Pendente', color: 'bg-amber-100 text-amber-700' },
  active: { label: 'Ativo', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelado', color: 'bg-slate-100 text-slate-500' },
  expired: { label: 'Expirado', color: 'bg-slate-100 text-slate-500' },
  rejected: { label: 'Rejeitado', color: 'bg-rose-100 text-rose-700' },
  suspended: { label: 'Suspenso (inadimplência)', color: 'bg-rose-100 text-rose-700' },
};

const formatBR = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
const formatMoney = (v) => `R$ ${(v ?? 0).toFixed(2).replace('.', ',')}`;

const AdminSponsorshipsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [partners, setPartners] = useState({});
  const [approveTarget, setApproveTarget] = useState(null);
  const [approvePrice, setApprovePrice] = useState('');
  const [approving, setApproving] = useState(false);
  const [credentialsResult, setCredentialsResult] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [spRes, parRes] = await Promise.all([
        axios.get(`${API}/admin/sponsorships`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/partners`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setItems(spRes.data.items || []);
      const map = {};
      (parRes.data || []).forEach(p => { map[p.id] = p; });
      setPartners(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar patrocínios');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openApprove = (sp) => {
    const partner = partners[sp.partner_id] || {};
    const defaultPrice = partner.sponsorship_price ?? 49.90;
    setApproveTarget(sp);
    setApprovePrice(String(defaultPrice));
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    const price = parseFloat(approvePrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Informe um preço válido');
      return;
    }
    setApproving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/sponsorships/${approveTarget.id}/approve`,
        { monthly_price: price },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.temp_password) {
        setCredentialsResult({
          email: approveTarget.owner_email,
          password: res.data.temp_password,
          company: approveTarget.sponsored_company_name,
          partner: (partners[approveTarget.partner_id] || {}).name,
        });
      } else {
        toast.success('Patrocínio aprovado! (Usuário já tinha conta, sem senha nova)');
      }
      setApproveTarget(null); setApprovePrice('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aprovar');
    } finally { setApproving(false); }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/sponsorships/${rejectTarget.id}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patrocínio rejeitado.');
      setRejectTarget(null); setRejectReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao rejeitar');
    }
  };

  const updatePartnerPrice = async (partnerId, newPrice) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/partners/${partnerId}/sponsorship-price`,
        { sponsorship_price: parseFloat(newPrice) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Preço padrão do parceiro atualizado');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const toggleSponsorshipConfig = async (partnerId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/partners/${partnerId}/sponsorship-config`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(value ? 'Ativado' : 'Desativado');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" /></div>;
  }

  const filtered = filter === 'all'
    ? items
    : filter === 'overdue'
      ? items.filter(i => i.status === 'active' && i.payment_status === 'overdue')
      : items.filter(i => i.status === filter);
  const counts = {
    all: items.length,
    pending_approval: items.filter(i => i.status === 'pending_approval').length,
    active: items.filter(i => i.status === 'active').length,
    overdue: items.filter(i => i.status === 'active' && i.payment_status === 'overdue').length,
    suspended: items.filter(i => i.status === 'suspended').length,
    cancelled: items.filter(i => i.status === 'cancelled').length,
  };

  return (
    <div className="space-y-6" data-testid="admin-sponsorships-tab">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <HandCoins className="w-7 h-7 text-[#C5A059]" /> Patrocínios B2B
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Parceiros patrocinam clientes com Plano 5 Estrelas. Aprove cada solicitação e defina o valor mensal cobrado.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'all', label: 'Todos' },
          { id: 'pending_approval', label: 'Aguardando' },
          { id: 'active', label: 'Ativos' },
          { id: 'overdue', label: 'Em atraso', danger: true },
          { id: 'suspended', label: 'Suspensos', danger: true },
          { id: 'cancelled', label: 'Cancelados' },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.id
                ? (f.danger ? 'bg-rose-600 text-white' : 'bg-[#0A2647] text-white')
                : (f.danger && counts[f.id] > 0 ? 'bg-rose-100 text-rose-700 hover:bg-rose-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
            }`}
            data-testid={`filter-${f.id}`}
          >
            {f.label} ({counts[f.id] ?? 0})
          </button>
        ))}
      </div>

      {/* Configuração por parceiro */}
      <Card className="border-0 shadow-md">
        <CardContent className="pt-5 pb-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Configuração por Parceiro</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {Object.values(partners).filter(p => p.status === 'active').map(p => (
              <PartnerConfigRow
                key={p.id}
                partner={p}
                onSavePrice={updatePartnerPrice}
                onToggle={toggleSponsorshipConfig}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      {filtered.length === 0 ? (
        <Card className="border-0 shadow-md">
          <CardContent className="py-12 text-center">
            <HandCoins className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nenhum patrocínio nesse filtro.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(sp => {
            const partner = partners[sp.partner_id] || {};
            const meta = STATUS_META[sp.status] || STATUS_META.pending_approval;
            return (
              <Card key={sp.id} className="border-0 shadow-md hover:shadow-lg transition-shadow" data-testid={`admin-sponsorship-${sp.id}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-11 h-11 rounded-lg bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <p className="text-base font-bold text-slate-900 truncate">{sp.sponsored_company_name}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
                          {sp.cnpj && <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" /> {sp.cnpj}</span>}
                          <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {sp.owner_email}</span>
                          {sp.owner_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {sp.owner_phone}</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 pt-1">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {sp.duration_months ? `${sp.duration_months} meses` : (<><InfinityIcon className="w-3 h-3" /> Indefinido</>)}
                          </span>
                          {sp.monthly_price ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-[#0A2647]">
                              <DollarSign className="w-3 h-3" /> {formatMoney(sp.monthly_price)}/mês
                            </span>
                          ) : null}
                          {sp.expires_at && <span className="text-slate-500">Vence em {formatBR(sp.expires_at)}</span>}
                        </div>
                        <p className="text-xs text-slate-500 pt-1">
                          <strong>Parceiro:</strong> {partner.name || sp.partner_name || '—'}
                          {sp.notes && <span className="block mt-1 italic">"{sp.notes}"</span>}
                        </p>
                        {(sp.has_existing_user || sp.has_existing_company) && sp.status === 'pending_approval' && (
                          <div className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <span>
                              {sp.has_existing_company ? 'Empresa com este CNPJ já existe — será promovida a patrocinada.' : 'E-mail do dono já cadastrado — será vinculado.'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${meta.color}`}>{meta.label}</span>
                      {sp.status === 'active' && sp.payment_status === 'overdue' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-300 text-rose-700 text-[10px] font-bold uppercase">
                          <AlertCircle className="w-2.5 h-2.5" /> Em atraso
                        </span>
                      )}
                      {sp.status === 'pending_approval' && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => openApprove(sp)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 h-8 text-xs" data-testid={`approve-${sp.id}`}>
                            <Check className="w-3 h-3" /> Aprovar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setRejectTarget(sp)} className="text-rose-600 border-rose-200 hover:bg-rose-50 gap-1 h-8 text-xs" data-testid={`reject-${sp.id}`}>
                            <X className="w-3 h-3" /> Rejeitar
                          </Button>
                        </div>
                      )}
                      {sp.status === 'cancelled' && sp.cancelled_reason && (
                        <span className="text-[10px] text-slate-500 italic">Motivo: {sp.cancelled_reason}</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Aprovar */}
      {approveTarget && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div>
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Aprovar patrocínio</p>
              <h3 className="text-base font-bold text-slate-900">{approveTarget.sponsored_company_name}</h3>
              <p className="text-xs text-slate-500">Parceiro: {(partners[approveTarget.partner_id] || {}).name}</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Valor mensal a cobrar do parceiro (R$)</Label>
              <Input type="number" step="0.01" value={approvePrice} onChange={e => setApprovePrice(e.target.value)} className="mt-1" data-testid="approve-price-input" />
              <p className="text-[11px] text-slate-500 mt-1">Cliente receberá Plano 5 Estrelas. Fatura individual será gerada no Asaas em nome do parceiro.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setApproveTarget(null)} className="flex-1" data-testid="cancel-approve">Cancelar</Button>
              <Button onClick={handleApprove} disabled={approving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="confirm-approve">
                {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirmar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Credenciais (pos-aprovacao) */}
      {credentialsResult && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="text-base font-bold text-slate-900">Patrocínio aprovado!</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Repasse estas credenciais para <strong>{credentialsResult.partner}</strong> entregar ao cliente:
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-4 space-y-3">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Empresa</p>
                <p className="text-sm font-semibold text-slate-900">{credentialsResult.company}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">E-mail (login)</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono font-semibold text-slate-900 flex-1 break-all" data-testid="cred-email">{credentialsResult.email}</p>
                  <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-xs" onClick={() => { navigator.clipboard.writeText(credentialsResult.email); toast.success('E-mail copiado'); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Senha temporária</p>
                <div className="flex items-center gap-2">
                  <p className="text-base font-mono font-bold text-[#C5A059] flex-1" data-testid="cred-password">{credentialsResult.password}</p>
                  <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-xs" onClick={() => { navigator.clipboard.writeText(credentialsResult.password); toast.success('Senha copiada'); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                <strong>Nenhum e-mail automático foi enviado ao cliente.</strong> O parceiro entrega as credenciais pessoalmente.
              </p>
            </div>

            <Button
              onClick={() => {
                const text = `Empresa: ${credentialsResult.company}\nE-mail: ${credentialsResult.email}\nSenha: ${credentialsResult.password}\nLogin: https://falocomodono.com.br/login`;
                navigator.clipboard.writeText(text);
                toast.success('Credenciais copiadas para a área de transferência');
              }}
              className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2"
              data-testid="copy-all-credentials"
            >
              <Copy className="w-4 h-4" /> Copiar tudo para enviar ao parceiro
            </Button>
            <Button
              variant="outline"
              onClick={() => setCredentialsResult(null)}
              className="w-full"
              data-testid="close-credentials"
            >
              Fechar
            </Button>
          </div>
        </div>
      )}

      {/* Modal Rejeitar */}
      {rejectTarget && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div>
              <p className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-1">Rejeitar patrocínio</p>
              <h3 className="text-base font-bold text-slate-900">{rejectTarget.sponsored_company_name}</h3>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Motivo (opcional)</Label>
              <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Ex: CNPJ inválido" className="mt-1" data-testid="reject-reason-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setRejectTarget(null)} className="flex-1">Voltar</Button>
              <Button onClick={handleReject} className="flex-1 bg-rose-600 hover:bg-rose-700 text-white gap-2" data-testid="confirm-reject">
                <XCircle className="w-4 h-4" /> Rejeitar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PartnerConfigRow = ({ partner, onSavePrice, onToggle }) => {
  const [price, setPrice] = useState(partner.sponsorship_price ?? 49.90);
  const [editing, setEditing] = useState(false);
  const autoApprove = !!partner.sponsorship_auto_approve;
  const blocked = !!partner.sponsorship_blocked;

  return (
    <div className={`p-3 rounded-lg border ${blocked ? 'border-rose-200 bg-rose-50/30' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-slate-800 truncate">{partner.name}</span>
          {autoApprove && !blocked && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase">
              <Zap className="w-2.5 h-2.5" /> Auto
            </span>
          )}
          {blocked && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase">
              <Ban className="w-2.5 h-2.5" /> Bloqueado
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <Input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} className="h-7 w-24 text-xs" />
              <Button size="sm" onClick={() => { onSavePrice(partner.id, price); setEditing(false); }} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                <Check className="w-3 h-3" />
              </Button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="text-xs font-semibold text-[#C5A059] hover:underline" data-testid={`price-${partner.id}`}>
              {formatMoney(partner.sponsorship_price ?? 49.90)}/mês
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-100 text-xs flex-wrap">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={autoApprove}
            onChange={e => onToggle(partner.id, 'sponsorship_auto_approve', e.target.checked)}
            className="rounded"
            data-testid={`auto-approve-${partner.id}`}
          />
          <span className="text-slate-700 font-medium">Aprovação automática (parceiro recebe credenciais na hora)</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={blocked}
            onChange={e => onToggle(partner.id, 'sponsorship_blocked', e.target.checked)}
            className="rounded"
            data-testid={`block-${partner.id}`}
          />
          <span className="text-slate-700 font-medium">Bloquear novos patrocínios</span>
        </label>
        {partner.panel_token && (
          <a
            href={`/parceiro/painel/${partner.panel_token}?tab=patrocinios&view=guia`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#0A2647] text-white hover:bg-[#0e3260] text-[11px] font-semibold"
            data-testid={`view-guide-${partner.id}`}
          >
            <BookOpen className="w-3 h-3" /> Ver/Baixar guia <ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
      </div>
    </div>
  );
};

export default AdminSponsorshipsTab;
